import React, { useState, useEffect, useRef } from 'react'
import classNames from 'classnames'
import { DateTime } from 'luxon'
import { Case, Default, Else, If, Switch, Then, When } from 'react-if'
import LazyHydrate from 'react-lazy-hydration'
import { InternalLink } from '@/atoms/InternalLink'
import { ClosedCaptionsBadge, ContentRatingBadge, HighDefinitionBadge } from '@/atoms/MetadataBadge'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { AsH1, AsH2, AsH4, CaptionMD, HeadingLG, HeadingXS, LabelSM } from '@/atoms/Text'
import { projectTypes } from '@/constants/projectTypes'
import { slugs } from '@/constants/slugs'
import { useProjectMeta } from '@/contexts/ProjectMetaContext'
import { BoundedImage } from '@/molecules/BoundedImage'
import { LoginToWatchButtonGroup } from '@/molecules/LoginToWatchButtonGroup'
import { PayItForwardButton } from '@/molecules/PayItForwardButton'
import { ComingSoonButton, ContentRestrictedButton, GeoBlockedButton } from '@/molecules/UnavailableButtons'
import { ResponsiveBackgroundContainer } from '@/organisms/WatchProjectHero/ResponsiveBackgroundContainer'
import {
  CatalogTitle,
  formatNonTitleLikeLandscapeImagePath,
  formatNonTitleLikeVerticalImagePath,
} from '@/services/ContentCatalog'
import { Episode, GetEpisodeResponse } from '@/services/ProjectsService'
import { ContentEpisode } from '@/services/ProjectsService/types'
import { useUser } from '@/services/UserService'
import { useLoginUrl } from '@/utils/auth-utils'
import { useTranslate, Translate } from '@/utils/translate/translate-client'

interface WatchEpisodeHeroProps {
  loading?: boolean
  data?: GetEpisodeResponse
  episode: Episode
  hasPayItForward?: boolean
  slug: string
  isGeoBlocked?: boolean
  isEpisodeEarlyAccess?: boolean
  isLoginRequired?: boolean
  watchableTitle?: CatalogTitle | null
  contentEpisode?: ContentEpisode
}

export const WatchEpisodeHero: React.FC<WatchEpisodeHeroProps> = ({
  loading,
  data,
  episode,
  hasPayItForward,
  slug,
  isGeoBlocked = false,
  isEpisodeEarlyAccess = false,
  isLoginRequired = false,
  watchableTitle = null,
  contentEpisode,
}) => {
  const contentRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslate('watch')
  const [expanded, setExpanded] = useState(false)
  const [isTruncated, setIsTruncated] = useState(false)
  const { metadata, projectType, releaseDate, contentRating, title } = useProjectMeta()
  const { loginUrl, trackLoginStarted } = useLoginUrl()
  const { isLoggedIn } = useUser()
  const releaseYear = releaseDate ? DateTime.fromISO(releaseDate).year : null

  const handleReadMoreOrLess = () => {
    setExpanded(!expanded)
  }

  useEffect(() => {
    const handleResize = () => {
      if (!loading && data) {
        const element = document.getElementById('show-description')

        if (element) {
          setIsTruncated(element.offsetHeight < element.scrollHeight || element.offsetWidth < element.scrollWidth)
        }
      }
    }
    window.addEventListener('resize', handleResize)
    handleResize()
    return () => window.removeEventListener('resize', handleResize)
  }, [loading, data])

  const projectSlug = data?.project?.slug
  const isGuildMemberContent = isEpisodeEarlyAccess
  const genres = contentEpisode?.genres ?? metadata?.genres

  const fallbackImage = data?.episode?.posterLandscapeCloudinaryPath || ''
  const isSeriesContent = projectType === projectTypes.series

  const backgroundImageUrl =
    (isSeriesContent && getSeriesLandscapeBackdrop(title)) ||
    (watchableTitle !== null && formatNonTitleLikeLandscapeImagePath(watchableTitle)) ||
    fallbackImage

  const backgroundTabletImageUrl =
    (isSeriesContent && getSeriesLandscapeBackdrop(title)) ||
    (watchableTitle !== null && formatNonTitleLikeLandscapeImagePath(watchableTitle)) ||
    fallbackImage

  const backgroundMobileImageUrl =
    (isSeriesContent && getSeriesSquarePortraitNonTitleArt(title)) ||
    (watchableTitle !== null && formatNonTitleLikeVerticalImagePath(watchableTitle, ['1:1', '4:3'])) ||
    fallbackImage

  return (
    <div
      className={classNames([
        'flex flex-col gap-4 relative w-full lg:flex-row lg:flex-wrap md:min-h-[50vh] lg:min-h-[70vh] xl:min-h-[75vh] 2xl:min-h-[630px] animate-fadein',
      ])}
      ref={contentRef}
    >
      <ResponsiveBackgroundContainer
        cloudinaryImgPath={backgroundImageUrl}
        cloudinaryTabletImgPath={backgroundTabletImageUrl}
        cloudinaryMobileImgPath={backgroundMobileImageUrl}
        theaterBackgroundPosition={'bg-top'}
        contentRef={contentRef}
      />
      <PaddedContainer fluid className="flex items-center justify-center">
        <div className="relative mt-[23vh] w-full pt-24 md:mt-10 md:max-w-[600px] xl:max-w-[650px]">
          <header>
            {data?.project?.name && (
              <HeadingLG
                as={AsH1}
                className={classNames('mb-4 text-center md:text-left', {
                  hidden: data?.project?.logoCloudinaryPath,
                })}
                weight="bold"
              >
                {data.project.name}
              </HeadingLG>
            )}
            {data?.project?.logoCloudinaryPath && (
              <BoundedImage
                className="mb-2 flex items-center justify-center md:justify-start"
                alt={data?.project?.name ?? contentEpisode?.name ?? episode?.name}
                path={data.project.logoCloudinaryPath}
                width={600}
                height={104}
              />
            )}

            <When condition={episode?.name && projectType !== 'movie' && projectType !== 'special'}>
              <HeadingXS as={AsH2} className="mb-2 text-center md:text-left" weight="bold">
                {contentEpisode?.name ?? episode.name}
              </HeadingXS>
            </When>
            <When condition={episode.subtitle && episode.subtitle !== data?.project?.name}>
              <HeadingXS as={AsH4} className="mb-2 text-center text-2xl md:text-left" weight="semibold">
                {episode.subtitle}
              </HeadingXS>
            </When>
          </header>
          <div className="mb-2 hidden max-w-[500px] flex-col items-center gap-2 md:flex md:items-start">
            <CaptionMD
              id="show-description"
              className={classNames('text-center md:text-left transition-all duration-600 ease-in-out', {
                'max-h-[60px] line-clamp-2': !expanded,
                'overflow-hidden max-h-96': expanded,
              })}
              weight="medium"
              color="core-gray-400"
            >
              {contentEpisode?.description ?? data?.episode?.description}
            </CaptionMD>
            <When condition={isTruncated}>
              <button onClick={handleReadMoreOrLess}>
                <CaptionMD
                  className="cursor-pointer transition-all duration-300 ease-in-out hover:brightness-110"
                  color="oxide-bright"
                  weight="medium"
                >
                  <If condition={expanded}>
                    <Then>
                      <Translate i18nKey="readLessv3" t={t}>
                        Read less
                      </Translate>
                    </Then>
                    <Else>
                      <Translate i18nKey="readMorev3" t={t}>
                        Read more
                      </Translate>
                    </Else>
                  </If>
                </CaptionMD>
              </button>
            </When>
          </div>
          <div className="mb-9 flex flex-row flex-wrap justify-center gap-2 text-core-gray-500 md:justify-start md:text-core-gray-300">
            {releaseYear && projectSlug !== slugs.dryBar && (
              <>
                <CaptionMD>{releaseYear}</CaptionMD>
                <CaptionMD>•</CaptionMD>
              </>
            )}
            <CaptionMD className="whitespace-nowrap">
              <Switch>
                <Case condition={projectType === projectTypes.series}>
                  <Translate i18nKey="tvSeries" t={t}>
                    TV Series
                  </Translate>
                </Case>
                <Case condition={projectType === projectTypes.movie}>
                  <Translate i18nKey="movie" t={t}>
                    Movie
                  </Translate>
                </Case>
                <Case condition={projectType === projectTypes.podcast}>
                  <Translate i18nKey="podcast" t={t}>
                    Podcast
                  </Translate>
                </Case>
                <Case condition={projectType === projectTypes.special}>
                  <Translate i18nKey="special" t={t}>
                    Special
                  </Translate>
                </Case>
              </Switch>
            </CaptionMD>
            {Array.isArray(genres) && genres.length > 0 && <CaptionMD>•</CaptionMD>}
            {Array.isArray(genres) &&
              genres.map((genre, index, arr) => (
                <div className="flex gap-2" key={genre}>
                  <CaptionMD>{genre}</CaptionMD>
                  <When condition={index < arr.length - 1}>
                    <CaptionMD>•</CaptionMD>
                  </When>
                </div>
              ))}
            <div className="flex flex-row gap-2 md:basis-full">
              <ContentRatingBadge
                contentRating={contentRating}
                className="rounded-xl border-0 bg-[#0000004d] px-3 py-1 text-white backdrop-blur-xl"
              />
              <HighDefinitionBadge
                slug={slug}
                className="rounded-xl border-0 bg-[#0000004d] px-3 py-1 text-white backdrop-blur-xl"
              />
              <ClosedCaptionsBadge className="rounded-xl border-0 bg-[#0000004d] px-3 py-1 text-white backdrop-blur-xl" />
            </div>
          </div>
          <div className="mt-10 flex flex-col md:flex-row">
            <Switch>
              <Case condition={isGeoBlocked}>
                <GeoBlockedButton className="w-full px-6 py-3 md:w-[220px]" />
              </Case>
              <Case condition={data?.episode?.unavailableReason === 'LIMITED_PROFILE'}>
                <ContentRestrictedButton />
              </Case>
              <Case
                condition={
                  contentEpisode?.watchableAt && new Date(contentEpisode.watchableAt) > new Date()
                    ? true
                    : data?.episode?.earlyAccessDate &&
                      // If publiclyAvailableDate is null, just check if earlyAccessDate is in the future
                      ((!data?.episode?.publiclyAvailableDate && new Date(data.episode.earlyAccessDate) > new Date()) ||
                        // If both dates exist and are equal, check if earlyAccessDate is in the future
                        (data?.episode?.publiclyAvailableDate &&
                          data.episode.earlyAccessDate === data.episode.publiclyAvailableDate &&
                          new Date(data.episode.earlyAccessDate) > new Date()))
                }
              >
                <ComingSoonButton />
              </Case>
              <Default>
                <LazyHydrate whenVisible>
                  <LoginToWatchButtonGroup
                    episode={episode}
                    isGuildMemberContent={isGuildMemberContent}
                    slug={contentEpisode?.project?.slug ?? data?.project?.slug ?? episode.projectSlug}
                    trailer={data?.project?.trailers?.[0]}
                    project={data?.project}
                    isLoggedIn={isLoggedIn}
                  />
                </LazyHydrate>
              </Default>
            </Switch>
            {hasPayItForward && projectSlug && !isLoginRequired && (
              <PayItForwardButton
                projectSlug={projectSlug}
                pifEnabled={true}
                utmSource="watch-episode-hero"
                className={
                  'mt-4 box-border h-8 min-h-[50px] w-full rounded-md backdrop-blur-sm md:ml-4 md:mt-0 md:w-fit'
                }
              />
            )}
          </div>
          <When condition={!isLoggedIn}>
            <When condition={isEpisodeEarlyAccess}>
              <LabelSM className="mt-4 text-center md:text-left">
                <Translate t={t} i18nKey="logInIfGuildMember">
                  Already a member of the Angel Guild?&nbsp;&nbsp;&nbsp;
                  <InternalLink
                    className="text-oxide-bright underline"
                    href={loginUrl}
                    onClick={() => trackLoginStarted('WatchEpisodeHero')}
                  >
                    Log In
                  </InternalLink>
                </Translate>
              </LabelSM>
            </When>
            <When condition={!isEpisodeEarlyAccess}>
              <LabelSM className="mt-4 text-center md:text-left">
                <Translate t={t} i18nKey="logInAlreadyHaveAccount">
                  Already have an account?&nbsp;&nbsp;&nbsp;
                  <InternalLink
                    className="text-oxide-bright underline"
                    href={loginUrl}
                    onClick={() => trackLoginStarted('WatchEpisodeHero')}
                  >
                    Log In
                  </InternalLink>
                </Translate>
              </LabelSM>
            </When>
          </When>
        </div>
      </PaddedContainer>
    </div>
  )
}

const getCloudinaryPath = (backdrop: unknown): string | null => {
  if (
    backdrop &&
    typeof backdrop === 'object' &&
    'cloudinaryPath' in backdrop &&
    typeof backdrop.cloudinaryPath === 'string'
  ) {
    return backdrop.cloudinaryPath
  }
  return null
}

const getSeriesLandscapeBackdrop = (title: unknown): string | null => {
  if (title && typeof title === 'object' && 'landscapeBackdrop' in title) {
    return getCloudinaryPath(title.landscapeBackdrop)
  }
  return null
}

const getSeriesSquarePortraitNonTitleArt = (title: unknown): string | null => {
  if (title && typeof title === 'object' && 'squarePortraitNonTitleArt' in title) {
    return getCloudinaryPath(title.squarePortraitNonTitleArt)
  }
  return null
}
